import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation } from 'react-router-dom';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import {
    LayoutDashboard,
    TrendingUp,
    CalendarClock,
    ArrowUpDown,
    Users,
    Megaphone,
    Briefcase,
    Truck,
    Menu,
    ChevronLeft,
    Sun,
    Moon,
    Home as HomeIcon,
    Timer,
    Building2,
    FileText,
    Database,
    Calendar,
    Settings,
    LogOut,
    LogIn,
    Mail,
    Lock,
    Euro,
    Calculator,
    Archive,
    Package,
    HeartPulse,
    Clock,
    Receipt,
    BarChart3,
    Link2,
    Wrench,
    Shield,
    Pencil,
    Activity,
    Copy,
    UserRound
} from 'lucide-react';

import Marketing from './Marketing';
import Sales from './Sales';
import People from './People';
import Delivery from './components/Delivery';
import Home from './components/Home';
import Timekeeping from './components/Timekeeping';
import PartnerReports from './components/PartnerReports';
import ExpiredIdReports from './components/ExpiredIdReports';
import ReviewCompanies from './components/ReviewCompanies';
import CroDiscrepancies from './components/CroDiscrepancies';
import CroDataDump from './components/CroDataDump';
import ClientTenureReport from './components/ClientTenureReport';
import ServiceReports from './components/ServiceReports';
import WorkActivityBreakdown from './components/WorkActivityBreakdown';
import ScheduledJobs from './components/ScheduledJobs';
import LegacyScheduledJobs from './components/LegacyScheduledJobs';
import ScheduledEmails from './components/ScheduledEmails';
import DraftXeroInvoices from './components/DraftXeroInvoices';
import EmployeeRates from './components/EmployeeRates';
import MasterServicesList from './components/MasterServicesList';
import MasterServicesEditor from './components/MasterServicesEditor';
import LegacyReports from './components/LegacyReports';
import ClientHealth from './components/ClientHealth';
import DebtTracking from './components/DebtTracking';
import BssReporting from './components/BssReporting';
import XeroCallbackHandler from './components/XeroCallbackHandler';
import EnhancedTimekeeping from './components/EnhancedTimekeeping';
import AcquisitionChargebacks from './components/AcquisitionChargebacks';
import HubspotDealsPipeline from './components/HubspotDealsPipeline';
import RenewalsWipFees from './components/RenewalsWipFees';
import ClientTierMovements from './components/ClientTierMovements';
import CorporationTaxTracker from './components/CorporationTaxTracker';
import DraftTimekeeping from './components/DraftTimekeeping';
import ClientLinkingAnalysis from './components/ClientLinkingAnalysis';
import XeroContactLinker from './components/XeroContactLinker';
import ClientDuplicates from './components/ClientDuplicates';
import CorkClients from './components/CorkClients';
import CorkUtilisation from './components/CorkUtilisation';
import TimeEntryCorrection from './components/TimeEntryCorrection';
import CachingPage from './Caching';
import Permissions from './components/Permissions';
import SystemReport from './components/SystemReport';
import XeroClients from './components/XeroClients';
import { useTelemetry, getPageName } from './hooks/useTelemetry';
import { getApiBaseUrl } from './utils/baseUrl';

import './styles/theme.css';
import './styles/components.css';

// Loading Context for global loading state
const LoadingContext = createContext<{
    loading: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}>({ loading: false, setLoading: () => {} });

export const useLoading = () => useContext(LoadingContext);
// MSAL configuration
const msalConfig = {
    auth: {
        clientId: "5c12d71d-325d-4917-8a64-47a5b172849e",
        authority: "https://login.microsoftonline.com/542622e0-cadd-4447-b68a-5c2094fb0ea9",
        redirectUri: window.location.origin
    },
    cache: {
        cacheLocation: "localStorage" as const,
        storeAuthStateInCookie: true
    }
};

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Clear ALL stuck MSAL interaction artifacts — session storage AND cookies.
 * This is necessary because `storeAuthStateInCookie: true` stores state in both,
 * and clearing only sessionStorage leaves the cookie-side stuck.
 */
const clearMsalInteractionState = () => {
    try {
        // Clear session storage entries
        const keys = Object.keys(sessionStorage);
        for (const key of keys) {
            if (key.startsWith('msal.') && (key.includes('interaction') || key.includes('request.'))) {
                sessionStorage.removeItem(key);
            }
        }
    } catch { /* sessionStorage may not be available */ }
    try {
        // Clear MSAL cookies (interaction status, nonce, state, etc.)
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
            const name = cookie.split('=')[0].trim();
            if (name.startsWith('msal.')) {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            }
        }
    } catch { /* cookie API may not be available */ }
};

// Module-level MSAL initialization — called once, before any component mounts.
// The promise is cached so even if referenced multiple times, init only runs once.
const msalInitPromise = (async () => {
    await msalInstance.initialize();
    try {
        const response = await msalInstance.handleRedirectPromise();
        if (response?.account) {
            msalInstance.setActiveAccount(response.account);
        }
    } catch (err: any) {
        // If redirect handling fails, clear ALL stuck interaction artifacts
        clearMsalInteractionState();
        if (err?.errorCode !== 'no_token_request_cache_error') {
            console.error('MSAL redirect error:', err);
        }
    }
})();

// Shared token acquisition - deduplicates concurrent acquireTokenSilent calls
let pendingTokenPromise: Promise<string | null> | null = null;
let redirectInFlight = false;

// Seconds until a JWT expires, without verifying it (verification is the
// server's job) — used only to decide when to force an MSAL refresh.
const tokenSecondsRemaining = (token: string): number => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return payload.exp - Math.floor(Date.now() / 1000);
    } catch {
        return 0;
    }
};

// When silent renewal needs user interaction (Conditional Access sign-in
// frequency, revoked session, ...), send the user through a redirect
// re-auth instead of silently failing every API call.
const beginInteractiveReauth = (instance: any) => {
    if (redirectInFlight) return;
    redirectInFlight = true;
    clearMsalInteractionState();
    instance.acquireTokenRedirect({ scopes: ["User.Read"] }).catch((e: any) => {
        redirectInFlight = false;
        console.error('Interactive re-auth failed:', e);
    });
};

// Token for calls to OUR API. This is the ID token (audience = this app's
// client id) — the backend verifies its signature against Entra's keys.
// The Graph access token must never be used here: its signature is not
// third-party-verifiable and its audience is Microsoft Graph.
export const acquireTokenSafe = async (instance: any, accounts: any[]): Promise<string | null> => {
    if (accounts.length === 0) return null;
    if (pendingTokenPromise) return pendingTokenPromise;

    pendingTokenPromise = (async () => {
        try {
            let response = await instance.acquireTokenSilent({
                scopes: ["User.Read"],
                account: accounts[0],
                forceRefresh: false,
            });
            // MSAL may serve a cached ID token that is about to lapse even
            // when the access token is still fresh — refresh it so the
            // server never rejects a stale one.
            if (!response.idToken || tokenSecondsRemaining(response.idToken) < 60) {
                response = await instance.acquireTokenSilent({
                    scopes: ["User.Read"],
                    account: accounts[0],
                    forceRefresh: true,
                });
            }
            return response.idToken || null;
        } catch (err: any) {
            if (err instanceof InteractionRequiredAuthError || err?.name === 'InteractionRequiredAuthError') {
                beginInteractiveReauth(instance);
            } else {
                console.error('Token acquisition failed:', err);
            }
            return null;
        } finally {
            pendingTokenPromise = null;
        }
    })();

    return pendingTokenPromise;
};

// Graph access token — only for endpoints that proxy Microsoft Graph on the
// user's behalf (/api/user). Sent as X-Graph-Token, never as Authorization.
export const acquireGraphTokenSafe = async (instance: any, accounts: any[]): Promise<string | null> => {
    if (accounts.length === 0) return null;
    try {
        const response = await instance.acquireTokenSilent({
            scopes: ["User.Read"],
            account: accounts[0],
            forceRefresh: false,
        });
        return response.accessToken || null;
    } catch (err: any) {
        if (err instanceof InteractionRequiredAuthError || err?.name === 'InteractionRequiredAuthError') {
            beginInteractiveReauth(instance);
        } else {
            console.error('Graph token acquisition failed:', err);
        }
        return null;
    }
};

interface UserData {
    displayName: string;
    photoUrl: string | undefined;
}

interface NavItemProps {
    to: string;
    icon: React.ReactNode;
    label: string;
    collapsed: boolean;
    restricted?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon, label, collapsed, restricted }) => {
    const location = useLocation();
    const isActive = location.pathname === to;
    
    return (
        <Link 
            to={to} 
            className={`nav-item ${isActive ? 'active' : ''} ${restricted ? 'restricted' : ''}`} 
            title={collapsed ? label : undefined}
        >
            <span className="nav-icon">
                {icon}
                {restricted && collapsed && <Lock size={10} className="restricted-lock-icon" />}
            </span>
            {!collapsed && (
                <>
                    <span>{label}</span>
                    {restricted && <Lock size={12} className="restricted-indicator" />}
                </>
            )}
        </Link>
    );
};

// Helper function to get auth token (uses shared safe acquisition)
const getAuthToken = async (instance: any, accounts: any[]) => {
    return acquireTokenSafe(instance, accounts);
};

const AppContent = () => {
    const { instance, accounts, inProgress } = useMsal();
    const msalAuthenticated = useIsAuthenticated();
    const { logLogin, logLogout, logPageView } = useTelemetry();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authChecked, setAuthChecked] = useState(false);
    const [userData, setUserData] = useState<UserData>({
        displayName: 'Loading...',
        photoUrl: undefined
    });
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const saved = localStorage.getItem('theme');
        return saved ? saved === 'dark' : true; // Default to dark
    });
    const [loginError, setLoginError] = useState<string | null>(null);
    const [userAccess, setUserAccess] = useState<{ routes: { path: string; hasAccess: boolean; restricted: boolean }[]; isAdmin: boolean } | null>(null);

    // Fetch user route permissions
    useEffect(() => {
        if (inProgress !== InteractionStatus.None) return;
        const fetchAccess = async () => {
            if (!isAuthenticated || accounts.length === 0) return;
            try {
                const token = await getAuthToken(instance, accounts);
                if (!token) return;
                const res = await fetch(`${getApiBaseUrl()}/api/permissions/my-access`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.ok) {
                    setUserAccess(await res.json());
                }
            } catch (err) {
                console.warn('Could not load user access:', err);
            }
        };
        fetchAccess();
    }, [isAuthenticated, accounts, instance, inProgress]);

    // Helper: check if user has access to a route
    const canAccess = (path: string): boolean => {
        if (!userAccess) return true; // not loaded yet, default open
        const route = userAccess.routes.find(r => r.path === path);
        if (!route) return true; // route not in registry
        return route.hasAccess;
    };

    // Helper: check if a route is restricted
    const isRestricted = (path: string): boolean => {
        if (!userAccess) return false;
        const route = userAccess.routes.find(r => r.path === path);
        return route?.restricted ?? false;
    };

    // Load theme preference from server when authenticated
    useEffect(() => {
        if (inProgress !== InteractionStatus.None) return;

        const loadThemePreference = async () => {
            if (!isAuthenticated || accounts.length === 0) return;
            
            try {
                const token = await getAuthToken(instance, accounts);
                if (!token) return;
                
                const response = await fetch('/api/preferences', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    const serverTheme = data.theme === 'dark';
                    if (serverTheme !== isDarkMode) {
                        setIsDarkMode(serverTheme);
                    }
                }
            } catch (err) {
                console.warn('Could not load theme preference from server:', err);
            }
        };
        
        loadThemePreference();
    }, [isAuthenticated, accounts, instance, inProgress]);

    useEffect(() => {
        // Apply theme class to body
        document.body.classList.remove('light', 'dark');
        document.body.classList.add(isDarkMode ? 'dark' : 'light');
        localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    }, [isDarkMode]);

    // Check authentication status once MSAL is idle (no interaction in progress)
    useEffect(() => {
        if (inProgress !== InteractionStatus.None) return;

        const checkAuthStatus = async () => {
            try {
                const allAccounts = instance.getAllAccounts();
                if (allAccounts.length > 0) {
                    const token = await acquireTokenSafe(instance, allAccounts);
                    if (token) {
                        setIsAuthenticated(true);
                        logLogin();
                    } else {
                        console.log("Silent token acquisition failed, user needs to sign in again");
                        setIsAuthenticated(false);
                    }
                } else {
                    setIsAuthenticated(false);
                }
            } catch (error) {
                console.error("Error checking auth status:", error);
                setIsAuthenticated(false);
            } finally {
                setAuthChecked(true);
            }
        };
        checkAuthStatus();
    }, [instance, inProgress]);

    // Fetch user data when authenticated
    useEffect(() => {
        if (inProgress !== InteractionStatus.None) return;

        const fetchUserData = async () => {
            if (!isAuthenticated || accounts.length === 0) return;
            
            try {
                const token = await acquireTokenSafe(instance, accounts);
                const graphToken = await acquireGraphTokenSafe(instance, accounts);
                if (!token || !graphToken) return;

                const response = await fetch(`${getApiBaseUrl()}/api/user`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'X-Graph-Token': graphToken
                    }
                });
                if (!response.ok) {
                    throw new Error(`Fetch failed: ${response.status} ${response.statusText}`);
                }
                
                const data = await response.json();
                setUserData({
                    displayName: data.displayName || 'User',
                    photoUrl: data.photoUrl
                });
            } catch (error) {
                console.error('Error fetching user data:', error);
                setUserData({ 
                    displayName: 'User', 
                    photoUrl: undefined 
                });
            }
        };

        fetchUserData();
    }, [instance, accounts, isAuthenticated, inProgress]);

    const handleLogin = async () => {
        if (inProgress !== InteractionStatus.None) {
            // Clear stuck interaction state (session storage + cookies) and reload
            clearMsalInteractionState();
            console.log('Cleared stuck MSAL interaction state — reloading');
            window.location.reload();
            return;
        }
        try {
            setLoginError(null);
            await instance.loginRedirect({
                scopes: ["User.Read"],
                redirectStartPage: window.location.origin + '/',
            });
        } catch (error: any) {
            if (error?.errorCode === 'interaction_in_progress') {
                clearMsalInteractionState();
                window.location.reload();
                return;
            }
            console.error("Login failed:", error);
            setLoginError(error?.message || 'Login failed. Please try again.');
        }
    };

    const handleLogout = () => {
        // Log logout telemetry before logging out
        logLogout();
        instance
            .logoutRedirect()
            .catch((error) => console.error("Logout failed:", error));
    };

    const toggleTheme = async () => {
        const newTheme = !isDarkMode;
        setIsDarkMode(newTheme);
        
        // Save preference to server
        try {
            const token = await getAuthToken(instance, accounts);
            if (token) {
                await fetch('/api/preferences', {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({ theme: newTheme ? 'dark' : 'light' })
                });
            }
        } catch (err) {
            console.warn('Could not save theme preference to server:', err);
        }
    };

    // Show loading while checking auth
    if (!authChecked) {
        return (
            <div className="login-page">
                <div className="login-container">
                    <div className="login-logo">
                        <span className="logo-text">Kinore Analytics</span>
                        <span className="beta-badge">Beta</span>
                    </div>
                    <p className="login-subtitle">Checking authentication...</p>
                </div>
            </div>
        );
    }

    // Show login page if not authenticated
    if (!isAuthenticated) {
        return (
            <div className="login-page">
                <div className="login-container">
                    <div className="login-logo">
                        <span className="logo-text">Kinore Analytics</span>
                        <span className="beta-badge">Beta</span>
                    </div>
                    <p className="login-subtitle">Internal reporting platform for data insights across departments</p>
                    <button className="login-button" onClick={handleLogin}>
                        <LogIn size={20} />
                        Sign In with Kinore
                    </button>
                    {loginError && (
                        <p style={{ color: '#f85149', fontSize: '14px', marginTop: '12px' }}>{loginError}</p>
                    )}
                    <p className="login-help">
                        Having issues?{' '}
                        <a href="mailto:ITSupport@kinore.com">
                            <Mail size={14} />
                            ITSupport@Kinore.com
                        </a>
                    </p>
                </div>
            </div>
        );
    }

    // Page view tracker component
    const PageTracker = () => {
        const location = useLocation();
        
        useEffect(() => {
            logPageView(location.pathname, getPageName(location.pathname));
        }, [location.pathname]);
        
        return null;
    };

    return (
        <LoadingContext.Provider value={{ loading, setLoading }}>
        <Router>
            <PageTracker />
            {/* Loading indicator */}
            {loading && <div className="global-loading-bar" />}
            
            {/* Header */}
            <header className="header">
                <div className="header-left">
                    <button 
                        className="sidebar-toggle" 
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        {sidebarCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
                    </button>
                    <div className="logo">
                        <span className="logo-text">Kinore Analytics</span>
                        <span className="beta-badge">Beta</span>
                    </div>
                </div>
                <div className="header-right">
                    <button 
                        className="header-btn" 
                        onClick={toggleTheme}
                        title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                    >
                        {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
                    </button>
                    <div className="user-info">
                        <span className="user-name">{userData.displayName}</span>
                        {userData.photoUrl ? (
                            <img 
                                src={userData.photoUrl} 
                                alt={userData.displayName}
                                className="user-avatar"
                            />
                        ) : (
                            <div 
                                className="user-avatar" 
                                style={{ 
                                    backgroundColor: 'var(--accent-blue)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: 600
                                }}
                            >
                                {userData.displayName.charAt(0).toUpperCase()}
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* Sidebar */}
            <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
                <nav className="sidebar-nav">
                    <div className="nav-section">
                        {!sidebarCollapsed && <div className="nav-section-title">Overview</div>}
                        <NavItem 
                            to="/" 
                            icon={<HomeIcon size={18} />} 
                            label="Home" 
                            collapsed={sidebarCollapsed}
                        />
                    </div>

                    <div className="nav-section">
                        {!sidebarCollapsed && <div className="nav-section-title">Clients</div>}
                        {canAccess('/clients') && <NavItem
                            to="/clients"
                            icon={<Building2 size={18} />}
                            label="Client Listing"
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/clients')}
                        />}
                        {canAccess('/cork-clients') && <NavItem
                            to="/cork-clients"
                            icon={<Building2 size={18} />}
                            label="Cork Clients"
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/cork-clients')}
                        />}
                        {canAccess('/client-health') && <NavItem
                            to="/client-health"
                            icon={<HeartPulse size={18} />}
                            label="Client Health"
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/client-health')}
                        />}
                        {canAccess('/client-tier-movements') && <NavItem
                            to="/client-tier-movements"
                            icon={<ArrowUpDown size={18} />}
                            label="Client Tier Movements"
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/client-tier-movements')}
                        />}
                        {canAccess('/client-tenure') && <NavItem
                            to="/client-tenure"
                            icon={<Clock size={18} />}
                            label="Client Tenure"
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/client-tenure')}
                        />}
                    </div>

                    <div className="nav-section">
                        {!sidebarCollapsed && <div className="nav-section-title">Operations</div>}
                        {canAccess('/enhanced-timekeeping') && <NavItem
                            to="/enhanced-timekeeping"
                            icon={<BarChart3 size={18} />}
                            label="Utilisation Report"
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/enhanced-timekeeping')}
                        />}
                        {canAccess('/acquisition-chargebacks') && <NavItem
                            to="/acquisition-chargebacks"
                            icon={<Euro size={18} />}
                            label="Acquisition Chargebacks"
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/acquisition-chargebacks')}
                        />}
                        {canAccess('/corporation-tax-tracker') && <NavItem
                            to="/corporation-tax-tracker"
                            icon={<Calculator size={18} />}
                            label="Corporation Tax Tracker"
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/corporation-tax-tracker')}
                        />}
                        {canAccess('/hubspot-deals-pipeline') && <NavItem
                            to="/hubspot-deals-pipeline"
                            icon={<TrendingUp size={18} />}
                            label="HubSpot Deals Pipeline"
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/hubspot-deals-pipeline')}
                        />}
                        {canAccess('/renewals-wip-fees') && <NavItem
                            to="/renewals-wip-fees"
                            icon={<CalendarClock size={18} />}
                            label="Renewals: WIP vs Fees"
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/renewals-wip-fees')}
                        />}
                        {canAccess('/employee-rates') && <NavItem
                            to="/employee-rates" 
                            icon={<Euro size={18} />} 
                            label="Employee Rates" 
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/employee-rates')}
                        />}
                        {canAccess('/master-services') && <NavItem 
                            to="/master-services" 
                            icon={<Package size={18} />} 
                            label="Master Services" 
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/master-services')}
                        />}
                    </div>

                    <div className="nav-section">
                        {!sidebarCollapsed && <div className="nav-section-title">Tools</div>}
                        {canAccess('/client-linking') && <NavItem 
                            to="/client-linking" 
                            icon={<Link2 size={18} />} 
                            label="Client Linking" 
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/client-linking')}
                        />}
                        {canAccess('/xero-contact-linker') && <NavItem 
                            to="/xero-contact-linker" 
                            icon={<Link2 size={18} />} 
                            label="Xero Contact Linker" 
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/xero-contact-linker')}
                        />}
                        {canAccess('/client-duplicates') && <NavItem 
                            to="/client-duplicates" 
                            icon={<Copy size={18} />} 
                            label="Client Duplicates" 
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/client-duplicates')}
                        />}
                        {canAccess('/debt-tracking') && <NavItem 
                            to="/debt-tracking" 
                            icon={<Euro size={18} />} 
                            label="Debt Tracking" 
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/debt-tracking')}
                        />}
                        {canAccess('/time-corrections') && <NavItem 
                            to="/time-corrections" 
                            icon={<Wrench size={18} />} 
                            label="Time Corrections" 
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/time-corrections')}
                        />}
                        {canAccess('/master-services-editor') && <NavItem 
                            to="/master-services-editor" 
                            icon={<Pencil size={18} />} 
                            label="Master Services Editor" 
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/master-services-editor')}
                        />}
                    </div>

                    <div className="nav-section">
                        {!sidebarCollapsed && <div className="nav-section-title">System</div>}
                        {canAccess('/scheduled-jobs') && <NavItem 
                            to="/scheduled-jobs" 
                            icon={<Settings size={18} />} 
                            label="Scheduled Jobs" 
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/scheduled-jobs')}
                        />}
                        {canAccess('/scheduled-emails') && <NavItem 
                            to="/scheduled-emails" 
                            icon={<Mail size={18} />} 
                            label="Scheduled Emails" 
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/scheduled-emails')}
                        />}
                        {canAccess('/permissions') && <NavItem 
                            to="/permissions" 
                            icon={<Shield size={18} />} 
                            label="Permissions" 
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/permissions')}
                        />}
                        {canAccess('/system-report') && <NavItem 
                            to="/system-report" 
                            icon={<Activity size={18} />} 
                            label="System Report" 
                            collapsed={sidebarCollapsed}
                            restricted={isRestricted('/system-report')}
                        />}
                        <button 
                            className="nav-item logout-btn" 
                            onClick={handleLogout}
                            title={sidebarCollapsed ? "Logout" : undefined}
                        >
                            <span className="nav-icon"><LogOut size={18} /></span>
                            {!sidebarCollapsed && <span>Logout</span>}
                        </button>
                    </div>
                </nav>
            </aside>

            {/* Main Content */}
            <main className={`main-content ${sidebarCollapsed ? 'expanded' : ''}`}>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/client-tenure" element={<ClientTenureReport />} />
                    <Route path="/legacy-reports" element={<LegacyReports />} />
                    <Route path="/legacy-scheduled-jobs" element={<LegacyScheduledJobs />} />
                    <Route path="/delivery" element={<Delivery />} />
                    <Route path="/timekeeping" element={<Timekeeping />} />
                    <Route path="/enhanced-timekeeping" element={<EnhancedTimekeeping />} />
                    <Route path="/acquisition-chargebacks" element={<AcquisitionChargebacks />} />
                    <Route path="/hubspot-deals-pipeline" element={<HubspotDealsPipeline />} />
                    <Route path="/renewals-wip-fees" element={<RenewalsWipFees />} />
                    <Route path="/client-tier-movements" element={<ClientTierMovements />} />
                    <Route path="/corporation-tax-tracker" element={<CorporationTaxTracker />} />
                    <Route path="/draft-timekeeping" element={<DraftTimekeeping />} />
                    <Route path="/time-corrections" element={<TimeEntryCorrection />} />
                    <Route path="/work-activity" element={<WorkActivityBreakdown />} />
                    <Route path="/partners" element={<PartnerReports />} />
                    <Route path="/service-reports" element={<ServiceReports />} />
                    <Route path="/employee-rates" element={<EmployeeRates />} />
                    <Route path="/debt-tracking" element={<DebtTracking />} />
                    <Route path="/master-services" element={<MasterServicesList />} />
                    <Route path="/master-services-editor" element={<MasterServicesEditor />} />
                    <Route path="/clients" element={<BssReporting />} />
                    <Route path="/client-linking" element={<ClientLinkingAnalysis />} />
                    <Route path="/xero-contact-linker" element={<XeroContactLinker />} />
                    <Route path="/client-duplicates" element={<ClientDuplicates />} />
                    <Route path="/cork-clients" element={<CorkClients />} />
                    <Route path="/cork-utilisation" element={<CorkUtilisation />} />
                    <Route path="/client-health" element={<ClientHealth />} />
                    <Route path="/review-companies" element={<ReviewCompanies />} />
                    <Route path="/expired-ids" element={<ExpiredIdReports />} />
                    <Route path="/cro-discrepancies" element={<CroDiscrepancies />} />
                    <Route path="/cro-data-dump" element={<CroDataDump />} />
                    <Route path="/people" element={<People />} />
                    <Route path="/marketing" element={<Marketing />} />
                    <Route path="/sales" element={<Sales />} />
                    <Route path="/scheduled-jobs" element={<ScheduledJobs />} />
                    <Route path="/scheduled-emails" element={<ScheduledEmails />} />
                    <Route path="/permissions" element={<Permissions />} />
                    <Route path="/system-report" element={<SystemReport />} />
                    <Route path="/metadata" element={<CachingPage />} />
                    <Route path="/xero-clients" element={<XeroClients />} />
                    <Route path="/xero-inventory" element={<DraftXeroInvoices />} />
                    <Route path="/api/xero/callback" element={<XeroCallbackHandler />} />
                </Routes>
            </main>
        </Router>
        </LoadingContext.Provider>
    );
};

// Wrapper component to handle MSAL initialization
const App = () => {
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        // Await the module-level init promise (runs once, already started)
        msalInitPromise.then(() => setIsInitialized(true));
    }, []);

    if (!isInitialized) {
        return (
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100vh',
                backgroundColor: '#0d1117',
                color: '#e6edf3'
            }}>
                <div className="spinner" style={{ marginRight: '12px' }}></div>
                Initializing...
            </div>
        );
    }

    return (
        <MsalProvider instance={msalInstance}>
            <AppContent />
        </MsalProvider>
    );
};

export default App;